// src/components/attachment/AttachmentSection.jsx
import React, { useEffect, useRef } from "react";
import styles from "./AttachmentSection.module.css";

// ✅ 조건별 자동 붙임 매핑 테이블 (기본값)
// key: "문서종류-카테고리-세부옵션"
// value: [{ name: "자료명", count: 부수 }, ...]
const AUTO_ATTACHMENT_MAP = {
  // 환경/소독 - 대금청구
  "income_draft-환경/소독-대금청구": [
    { name: "청구서", count: 1 },
    { name: "소독증명서", count: 1 },
    { name: "서비스리포트", count: 1 },
    { name: "4대 사회보험 완납증명서", count: 1 },
    { name: "사업자등록증", count: 1 },
    { name: "통장사본", count: 1 },
    { name: "세금계산서(별도 전자전송)", count: 1 },
  ],
  // 환경/종합 - 대금청구
  "income_draft-환경/종합-대금청구": [
    { name: "작업계획서", count: 1 },
  ],
  // 환경/종합 - 개인고객 대금청구
  "income_draft-환경/종합-개인고객 대금청구": [
    { name: "작업계획서", count: 1 },
  ],
  // 환경/소독 - 기성계
  "income_draft-환경/소독-기성계": [
    { name: "기성검사원", count: 1 },
    { name: "기성내역서", count: 1 },
  ],
  // 환경/종합 - 기성계
  "income_draft-환경/종합-기성계": [
    { name: "기성검사원", count: 1 },
    { name: "기성내역서", count: 1 },
  ],
  // 환경/소독 - 완수계
  "income_draft-환경/소독-완수계": [
    { name: "완수검사원", count: 1 },
    { name: "완수내역서", count: 1 },
  ],
  // 환경/종합 - 완수계
  "income_draft-환경/종합-완수계": [
    { name: "완수검사원", count: 1 },
    { name: "완수내역서", count: 1 },
  ],
  // 환경/소독 - 준공계
  "income_draft-환경/소독-준공계": [
    { name: "준공검사원", count: 1 },
    { name: "준공내역서", count: 1 },
  ],
  // 환경/종합 - 준공계
  "income_draft-환경/종합-준공계": [
    { name: "준공검사원", count: 1 },
    { name: "준공내역서", count: 1 },
  ],

  // 용역사업 - 완수계
  "income_draft-용역사업-완수계": [
    { name: "완수검사원", count: 1 },
    { name: "완수내역서", count: 1 },
  ],
  // 용역사업 - 준공계
  "income_draft-용역사업-준공계": [
    { name: "준공검사원", count: 1 },
    { name: "준공내역서", count: 1 },
  ],
  // 경영팀 - 수입보고
  "income_draft-경영팀-수입보고": [
    { name: "수입증빙서류", count: 1 },
  ],

  // ── 지출기안 예시 (필요시 추가) ──
  // "expense_draft-경영팀-": [
  //   { name: "세금계산서", count: 1 },
  //   { name: "거래명세서", count: 1 },
  // ],
};

// ✅ 거래처별 붙임 예외 매핑
// key: "문서종류-카테고리-세부옵션"
// value: { 거래처명: [붙임 목록] }
const VENDOR_ATTACHMENT_OVERRIDE = {
  "income_draft-환경/소독-대금청구": {
    "희망학교": [
      { name: "소독증명서", count: 1 },
    ],
    "CLC희망학교": [
      { name: "소독증명서", count: 1 },
    ],
  },
  // 필요시 다른 조합도 추가 가능
  // "income_draft-환경/종합-대금청구": {
  //   "거래처명": [{ name: "...", count: 1 }],
  // },
};

/**
 * 붙임 자료 입력 컴포넌트
 * 
 * Props:
 * - attachments: [{ id, name, count }, ...]
 * - setAttachments: 상태 setter
 * - documentType: "income_draft" | "expense_draft" | ...
 * - category: "환경/소독" | "환경/종합" | ...
 * - detailOption: "대금청구" | "기성계" | ...
 * - vendor: 거래처명 (거래처별 예외 처리용)
 * - autoFill: true/false (자동 채우기 활성화 여부, 기본 true)
 */
export default function AttachmentSection({
  attachments,
  setAttachments,
  documentType = "",
  category = "",
  detailOption = "",
  vendor = "",
  autoFill = true,
}) {
  // ✅ 이전 키를 추적해서 조건 변경 시에만 자동 채우기
  const prevKeyRef = useRef("");

  // ✅ 조건 변경 시 자동 붙임 채우기
  useEffect(() => {
    if (!autoFill) return;

    const key = `${documentType}-${category}-${detailOption}`;
    const fullKey = `${key}-${vendor}`;
    
    if (fullKey === prevKeyRef.current) return;
    prevKeyRef.current = fullKey;

    let autoItems = null;

    // ⭐ [추가] 0순위: 어린이집 키워드 포함 시 4종 세트 자동 적용
    if (key === "income_draft-환경/소독-대금청구" && vendor.includes("어린이집")) {
      autoItems = [
        { name: "소독증명서", count: 1 },
        { name: "4대 사회보험 완납증명서", count: 1 },
        { name: "사업자등록증", count: 1 },
        { name: "거래명세서", count: 1 },
      ];
    } 

    // ✅ 1순위: 거래처별 예외 매핑 확인 (기존 희망학교 등)
    if (!autoItems) {
      const vendorOverrides = VENDOR_ATTACHMENT_OVERRIDE[key];
      if (vendorOverrides && vendor) {
        autoItems = vendorOverrides[vendor] || null;
      }
    }

    // ✅ 2순위: 기본 매핑 (일반 소독 7종 등)
    if (!autoItems) {
      autoItems = AUTO_ATTACHMENT_MAP[key];
    }

    if (autoItems && autoItems.length > 0) {
      const newAttachments = autoItems.map((item, idx) => ({
        id: idx + 1,
        name: item.name,
        count: item.count,
      }));
      setAttachments(newAttachments);
    }
  }, [documentType, category, detailOption, vendor, autoFill, setAttachments]);




  // ✅ 붙임 자료 핸들러
  const handleNameChange = (id, value) => {
    setAttachments((prev) =>
      prev.map((att) => (att.id === id ? { ...att, name: value } : att))
    );
  };

  const handleCountChange = (id, value) => {
    const num = value.replace(/[^\d]/g, "");
    setAttachments((prev) =>
      prev.map((att) =>
        att.id === id ? { ...att, count: num ? parseInt(num) : "" } : att
      )
    );
  };

  const handleAdd = () => {
    const nextId =
      attachments.length > 0
        ? Math.max(...attachments.map((a) => a.id)) + 1
        : 1;
    setAttachments((prev) => [...prev, { id: nextId, name: "", count: 1 }]);
  };

  const handleRemove = (id) => {
    if (attachments.length <= 1) return;
    setAttachments((prev) => prev.filter((att) => att.id !== id));
  };

  return (
    <div className={styles.attachmentSection}>
      {/* 1번 항목: 붙임 라벨 + 입력 + 추가버튼 한 줄 */}
      <div className={styles.attachmentHeader}>
        <div className={styles.attachmentLabel}>붙임</div>
        <span className={styles.attachmentNumber}>1.</span>
        <input
          type="text"
          placeholder="자료명 입력"
          value={attachments[0]?.name || ""}
          onChange={(e) => handleNameChange(attachments[0]?.id, e.target.value)}
          className={styles.attachmentNameInput}
        />
        <input
          type="text"
          inputMode="numeric"
          value={attachments[0]?.count || ""}
          onChange={(e) =>
            handleCountChange(attachments[0]?.id, e.target.value)
          }
          className={styles.attachmentCountInput}
        />
        <span className={styles.attachmentUnit}>부</span>
        <button
          type="button"
          onClick={handleAdd}
          className={styles.attachmentAddBtn}
        >
          +
        </button>
      </div>

      {/* 2번 이후 항목 */}
      {attachments.slice(1).map((att, index) => (
        <div key={att.id} className={styles.attachmentRow}>
          <span className={styles.attachmentNumber}>{index + 2}.</span>
          <input
            type="text"
            placeholder="자료명 입력"
            value={att.name}
            onChange={(e) => handleNameChange(att.id, e.target.value)}
            className={styles.attachmentNameInput}
          />
          <input
            type="text"
            inputMode="numeric"
            value={att.count}
            onChange={(e) => handleCountChange(att.id, e.target.value)}
            className={styles.attachmentCountInput}
          />
          <span className={styles.attachmentUnit}>부</span>
          <button
            type="button"
            onClick={() => handleRemove(att.id)}
            className={styles.attachmentRemoveBtn}
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}